import React, { useState } from "react";
import GetAppIcon from '@material-ui/icons/GetApp';
import { IconButton } from "@material-ui/core";
import ReportSettings from "../ReportSettings";
import { createDeviceReport } from "../../store/deviceStore/deviceRequests";
import { useTypedDispatch } from "../../app/state";
import { GenericModal } from "../../app/components/ui/generic-modal/GenericModal";
import { ReportingFieldList } from "./reportingFieldList";
import { isEmpty } from "lodash/fp";
import { useFormik } from "formik";
import { ExportSettingsValidationSchema } from "../../containers/SettingsPage/ExportSettings/ExportSettingsForm";
import { useSelector } from "react-redux";
import { selectGlobalSettings } from "../../store/settingsStore/settingsSelectors";

const SAVED_KEY = 'reporting';

export function loadSavedReportingParams() {
  const saved = localStorage.getItem(SAVED_KEY)
  return saved ? JSON.parse(saved) : null;
}

function getInitialValues(settings) {
  const savedSettings = loadSavedReportingParams() || {};
  const defaultValues = getDefaultValues(settings);
  return {
    fieldList: savedSettings.fieldList || defaultValues.fieldList,
    exportDefaultUnits: savedSettings.exportDefaultUnits || defaultValues?.exportDefaultUnits,
    exportEnumerationDivider: savedSettings.exportEnumerationDivider || defaultValues?.exportEnumerationDivider,
    exportFieldDivider: savedSettings.exportFieldDivider || defaultValues?.exportFieldDivider,
    exportDecimalDivider: savedSettings.exportDecimalDivider || defaultValues?.exportDecimalDivider,
    exportDecimalPlaceDigits: savedSettings.exportDecimalPlaceDigits || defaultValues?.exportDecimalPlaceDigits,
    exportFormatDate: savedSettings.exportFormatDate || defaultValues?.exportFormatDate,
  }
}

function getDefaultValues(settings) {
  return {
    fieldList: ReportingFieldList,
    exportDefaultUnits: settings?.exportDefaultUnits || 'MHz',
    exportEnumerationDivider: settings?.exportEnumerationDivider || ',',
    exportFieldDivider: settings?.exportFieldDivider || ';',
    exportDecimalDivider: settings?.exportDecimalDivider || '.',
    exportDecimalPlaceDigits: settings?.exportDecimalPlaceDigits || 2,
    exportFormatDate: settings?.exportFormatDate || 'DD.MM.YYYY',
  }
}

function saveReportValues(data) {
  localStorage.setItem(SAVED_KEY, JSON.stringify(data));
}

function sortFields(settings) {
  const result: string[] = [];

  ReportingFieldList.forEach((key) => {
    if (settings.fieldList.includes(key)) {
      result.push(key);
    }
  });

  settings.fieldList = result;
}

const ReportingDialog = ({ deviceList }) => {
  const dispatch = useTypedDispatch();
  const settings = useSelector(selectGlobalSettings);
  const [isOpened, setIsOpened] = useState(false);

  const settingsFormik = useFormik({
    initialValues: getInitialValues(settings),
    onSubmit: () => {},
    validationSchema: ExportSettingsValidationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const onOpen = () => {
    setIsOpened(true);
  }

  const onClose = () => {
    setIsOpened(false);
  }

  const onReport = (settings) => {
    const deviceIdList: number[] = [];
    deviceList.forEach((device) => device && deviceIdList.push(device.id));
    sortFields(settings);
    dispatch(createDeviceReport({ deviceIdList, settings }))
      .then((action) => {
        const csv = action.payload as string;
        const encodedUri = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "report.csv");
        document.body.appendChild(link); // Required for FF

        link.click();

        document.body.removeChild(link);
      })
  }

  const onSave = async () => {
    const errors = await settingsFormik.validateForm();
    if (isEmpty(errors)) {
      saveReportValues(settingsFormik.values);
    }

    return errors;
  }

  const onClear = () => {
    localStorage.removeItem(SAVED_KEY);
    settingsFormik.setErrors({});
    settingsFormik.setValues(getDefaultValues(settings));
  }

  const saveAndReport = async () => {
    const errors = await onSave();

    if (isEmpty(errors)) {
      onReport(settingsFormik.values);
    }
  }

  return (
    <>
      <IconButton
        aria-label='reporting'
        color='secondary'
        size='small'
        onClick={onOpen}
      >
        <GetAppIcon />
      </IconButton>
      <GenericModal
        isOpen={isOpened}
        closeModal={onClose}
        hideTitleSection
        title="Report"
        fullSize
        actionList={[
          {
            title: 'Clear',
            handler: onClear,
            color: 'secondary',
          },
          {
            title: 'Save',
            handler: onSave,
            color: 'secondary',
          },
          {
            title: 'Close',
            handler: onClose,
            color: 'secondary',
          },
          {
            title: 'Save & Report',
            handler: saveAndReport,
            color: 'primary',
          },
        ]}
      >
        <ReportSettings settingsFormik={settingsFormik} />
      </GenericModal>
    </>
  );
}

ReportingDialog.getInitialValues = getInitialValues;
ReportingDialog.saveReportValues = saveReportValues;

export default ReportingDialog;